import {
  Button,
  Checkbox,
  Icon,
  Input,
  Select,
  Shadow,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { debounce } from 'lodash';
import { Box, Pressable, ScrollView } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  NativeSyntheticEvent,
  TextInputChangeEventData,
  TextInputFocusEventData,
} from 'react-native';
import { useRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { CustomerFinancialResponseNormalized } from '../../../../../../store/customer/types';
import {
  batchActionState,
  modalSelectedItemsState,
} from '../../../../../../store/ProducManagement/atom';
import {
  ProductsManagementFixedIncomeProductsQuery,
  ProductsManagementSearchClientsOrAdvisorQuery,
} from '../../../../../../store/ProducManagement/selector';
import { ProductsManagementAdvisorResponse } from '../../../../../../store/ProducManagement/types';
import { ProductType } from '../../../../constants';
import { DestinationTypes, PlatformList } from '../../constants';

interface BlockModalFormProps {
  goBack: () => void;
  productType: ProductType;
}
export const BlockModalForm = ({ goBack, productType }: BlockModalFormProps) => {
  const [destination, setDestination] = useState<string>(DestinationTypes[0].value);
  const [platform, setPlatform] = useState<string>(PlatformList[0].value);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [clientAdvisorNameId, setClientAdvisorNameId] = useState<
    { id: number; name: string } | undefined
  >(undefined);
  const [autoCompleteSearchTerm, setAutoCompleteSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [fixedIncomeProductsSelected, setFixedIncomeProductsSelected] = useState<
    { id: number; name: string }[]
  >([]);

  const [, setModalSelectedItems] = useRecoilState(modalSelectedItemsState);
  const [, setBatchActionItems] = useRecoilState(batchActionState);

  const isClientChosen = destination === DestinationTypes[2].value;
  const isAdvisorChosen = destination === DestinationTypes[1].value;
  const isFixedIncome = productType === ProductType.FIXED_INCOME;
  const hasSearch = searchTerm.length >= 3;
  const selectedProductIds = useMemo(
    () => fixedIncomeProductsSelected.map((item) => item.id.toString()),
    [fixedIncomeProductsSelected]
  );

  const isFormValid = useMemo(() => {
    const hasDestination = !!destination;
    const hasPlatform = !!platform;
    const hasSelection = isClientChosen || isAdvisorChosen ? !!clientAdvisorNameId : true;
    const hasProductsSelected = isFixedIncome ? fixedIncomeProductsSelected.length > 0 : true;

    return hasDestination && hasPlatform && hasSelection && hasProductsSelected;
  }, [
    destination,
    platform,
    isClientChosen,
    isAdvisorChosen,
    clientAdvisorNameId,
    fixedIncomeProductsSelected,
  ]);

  const { data: currentCustomers, loading: searchingCustomers } = useRecoilQuery(
    ProductsManagementSearchClientsOrAdvisorQuery({
      searchTerm: autoCompleteSearchTerm,
      searchType: isAdvisorChosen ? 'advisor' : isClientChosen ? 'client' : undefined,
    })
  );

  const { data: currentFixedIncomeProducts, loading: loadingFixedIncomeProducts } = useRecoilQuery(
    ProductsManagementFixedIncomeProductsQuery
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedAutoCompleteSearchTerm = useCallback(
    debounce((newSearchTerm: string) => {
      setAutoCompleteSearchTerm(newSearchTerm);
    }, 1000),
    []
  );

  useEffect(() => {
    if (hasSearch) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [currentCustomers]);

  const handleSubmit = (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
    const text = e.nativeEvent.text;
    setSearchTerm(text);

    if (text.length >= 2) {
      setDebouncedAutoCompleteSearchTerm(text);
    } else {
      setShowDropdown(false);
      setAutoCompleteSearchTerm('');
    }
  };

  const handleClearSearchTerm = useCallback(() => {
    setClientAdvisorNameId(undefined);
    setSearchTerm('');
    setAutoCompleteSearchTerm('');
    setShowDropdown(false);
  }, []);

  const handleInputBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    if (!e.nativeEvent.text) {
      handleClearSearchTerm();
    }
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleInputFocus = () => {
    if (hasSearch) {
      setShowDropdown(true);
    }
  };

  const handleChangeDestination = (value: string) => {
    setDestination(value);
    setSearchTerm('');
    setAutoCompleteSearchTerm('');
    setClientAdvisorNameId(undefined);
    setShowDropdown(false);
  };

  const handleSelect = useCallback((selectedName: string, IdSelected: number) => {
    setSearchTerm(selectedName);
    setClientAdvisorNameId({ id: IdSelected, name: selectedName });
    setShowDropdown(false);
    setAutoCompleteSearchTerm('');
  }, []);

  const DropdownItemClient = useCallback(
    ({ item }: { item: CustomerFinancialResponseNormalized }) => (
      <Pressable
        onPress={() => handleSelect(item.customerNameNormalized, item.accountId)}
        p={Spacing['inline.xxxs']}
        py={Spacing['inline.quarck']}
        _pressed={{ backgroundColor: Color['neutral.medium'] }}
        _hover={{ backgroundColor: Color['brand.tertiary.lightest'], borderRadius: BoxRadius.sm }}>
        <VStack flex={1} maxWidth="100%" space={Spacing['inline.quarck']}>
          <Text ellipsizeMode="tail" numberOfLines={1} variant="semibold" flex={1}>
            {item.customerNameNormalized}
          </Text>
          <HStack justifyContent="space-between" space={Spacing['stack.xxxs']}>
            <Text color="neutral.medium" fontSize={Typography['fontsize.sm']}>
              {item.cpfCnpjNormalized}
            </Text>
            <Text color="neutral.medium" fontSize={Typography['fontsize.sm']}>
              {item.accountNumber} - {item.accountDigit}
            </Text>
          </HStack>
        </VStack>
      </Pressable>
    ),
    [handleSelect]
  );

  const DropdownItemAdvisor = useCallback(
    ({ item }: { item: ProductsManagementAdvisorResponse }) => (
      <Pressable
        onPress={() =>
          handleSelect(`${item.resourceFirstName} ${item.resourceLastName}`, item.advisorIntranetId)
        }
        p={Spacing['inline.xxxs']}
        py={Spacing['inline.quarck']}
        _pressed={{ backgroundColor: Color['neutral.medium'] }}
        _hover={{ backgroundColor: Color['brand.tertiary.lightest'], borderRadius: BoxRadius.sm }}>
        <VStack flex={1} maxWidth="100%" space={Spacing['inline.quarck']}>
          <Text ellipsizeMode="tail" numberOfLines={1} variant="semibold" flex={1}>
            {item.resourceFirstName} {item.resourceLastName}
          </Text>
          <HStack justifyContent="space-between" space={Spacing['stack.xxxs']}>
            <Text variant="regular" color="neutral.medium" fontSize={Typography['fontsize.sm']}>
              {item.contactEmail} ({item.document})
            </Text>
          </HStack>
        </VStack>
      </Pressable>
    ),
    [handleSelect]
  );

  const renderFields = useCallback(() => {
    if (isClientChosen || isAdvisorChosen) {
      return (
        <VStack flex={1} w="full" space={Spacing['inline.nano']}>
          <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.darkest">
            {isClientChosen
              ? 'Selecione o CLIENTE que deseja bloquear'
              : 'Selecione o ASSESSOR que deseja bloquear'}
          </Text>

          <Box position="relative" w="full">
            <Input
              placeholder="Pesquise por Nome, sobrenome, CPF ou email"
              noValidate={true}
              value={searchTerm}
              name="searchTerm"
              onChange={handleSubmit}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              autoComplete="off"
              hasShadow={false}
              variant="filled"
              inputRightElement={
                searchingCustomers ? (
                  <VStack px={15}>
                    <ActivityIndicator color={Color['brand.tertiary.light']} />
                  </VStack>
                ) : (
                  <Pressable
                    onPress={() => {
                      hasSearch ? handleClearSearchTerm() : null;
                    }}
                    pr={hasSearch ? 3 : 4}>
                    <Icon name={hasSearch ? 'Close' : 'Search'} size="xxxs" />
                  </Pressable>
                )
              }
            />

            {showDropdown && (
              <Shadow
                backgroundColor={Color['neutral.white']}
                position="absolute"
                p={Spacing['inline.nano']}
                top={10}
                left={0}
                right={0}
                level={2}
                borderRadius={BoxRadius.sm}>
                {currentCustomers?.data?.length ? (
                  <ScrollView maxHeight={200} showsVerticalScrollIndicator={false}>
                    <VStack space={Spacing['inline.quarck']}>
                      {isAdvisorChosen &&
                        currentCustomers.data.map((advisor) => (
                          <DropdownItemAdvisor key={advisor.resourceId} item={advisor} />
                        ))}
                      {isClientChosen &&
                        currentCustomers.data.map((customer) => (
                          <DropdownItemClient key={customer.cpfCnpjNormalized} item={customer} />
                        ))}
                    </VStack>
                  </ScrollView>
                ) : (
                  !searchingCustomers &&
                  currentCustomers?.data?.length === 0 &&
                  !clientAdvisorNameId && (
                    <Box px={Spacing['inline.nano']} py={Spacing['inline.quarck']}>
                      <VStack py={3} space={Spacing['inline.quarck']}>
                        <Text
                          variant="semibold"
                          fontSize={Typography['fontsize.md']}
                          color="neutral.darkest">
                          Nenhum resultado corresponde à sua pesquisa.
                        </Text>
                        <Text
                          variant="regular"
                          fontSize={Typography['fontsize.md']}
                          color="neutral.darkest">
                          Verifique seu nível de acesso e tente novamente.
                        </Text>
                      </VStack>
                    </Box>
                  )
                )}
              </Shadow>
            )}
          </Box>
        </VStack>
      );
    }

    return null;
  }, [
    isClientChosen,
    isAdvisorChosen,
    searchTerm,
    hasSearch,
    showDropdown,
    searchingCustomers,
    currentCustomers?.data,
    handleSubmit,
    handleInputBlur,
    handleInputFocus,
    handleClearSearchTerm,
    DropdownItemClient,
    DropdownItemAdvisor,
  ]);

  const handleConfirm = async () => {
    setModalSelectedItems({
      destinationId: Number(destination),
      platformId: Number(platform),
      clientOrAdvisorNameId: clientAdvisorNameId,
      isClientOrAdvisor: isAdvisorChosen ? 'advisor' : isClientChosen ? 'client' : undefined,
      productList: isFixedIncome ? fixedIncomeProductsSelected : undefined,
    });

    goBack();
  };

  const handleClose = async () => {
    setModalSelectedItems(null);
    setBatchActionItems(null);
    goBack();
  };

  const handleProductsSelect = (value: string) => {
    setFixedIncomeProductsSelected((prev) => {
      const product = currentFixedIncomeProducts?.find((p) => p.productId.toString() === value);
      const productObj = {
        id: Number(value),
        name: product?.ProductName ?? 'Produto não encontrado',
      };

      const exists = prev.some((item) => item.id === productObj.id);

      if (exists) {
        return prev.filter((item) => item.id !== productObj.id);
      } else {
        return [...prev, productObj];
      }
    });
  };

  return (
    <VStack
      backgroundColor={Color['neutral.white']}
      pt={{ base: Spacing['inline.nano'], lg: 0 }}
      px={Spacing['inline.xxxs']}
      pb={Spacing['inline.xxxs']}
      space={Spacing['stack.xxxs']}>
      <VStack flex={1} w="full" space={Spacing['inline.nano']}>
        <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.darkest">
          Destino de ação
        </Text>
        <Select
          triggerProps={{ bg: Color.background }}
          options={DestinationTypes}
          selectedValue={destination}
          onValueChange={handleChangeDestination}
          placeholder="Selecione o destino da ação"
        />
      </VStack>

      <VStack flex={1} w="full" space={Spacing['inline.nano']}>
        <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.darkest">
          Plataforma
        </Text>
        <Select
          triggerProps={{ bg: Color.background }}
          options={PlatformList}
          selectedValue={platform}
          onValueChange={setPlatform}
          placeholder="Selecione a plataforma"
        />
      </VStack>

      {renderFields()}

      {isFixedIncome && !loadingFixedIncomeProducts && (
        <VStack w="full" space={Spacing['inline.nano']} zIndex={-1}>
          <Text variant="semibold" fontSize={Typography['fontsize.md']} color="neutral.darkest">
            Produtos
          </Text>
          <Box
            w="full"
            bg={Color.background}
            borderRadius={BoxRadius.xs}
            p={Spacing['inline.xxxs']}>
            <ScrollView maxHeight={288}>
              <VStack justifyContent="flex-start" space={Spacing['inline.nano']}>
                {currentFixedIncomeProducts?.map((item, index) => (
                  <Checkbox
                    key={index}
                    optionPadding={{ bottom: 0, right: 0 }}
                    options={[{ label: item.ProductName, value: item.productId.toString() }]}
                    onChange={handleProductsSelect}
                    testId="checkbox-select-all"
                    selectedValues={selectedProductIds}
                  />
                ))}
              </VStack>
            </ScrollView>
          </Box>
        </VStack>
      )}

      <VStack
        direction={{ base: 'column', lg: 'row' }}
        bg="neutral.white"
        alignItems={'center'}
        justifyContent={'space-between'}
        w={'full'}
        space={Spacing['inline.nano']}
        zIndex={-1}>
        <Button
          variant="secondary"
          w={{ base: 'full', lg: 'container' }}
          flex={1}
          onPress={handleClose}
          _text={{
            color: 'neutral.darkest',
            fontFamily: Typography['fontfamily.base.semibold'],
          }}>
          CANCELAR
        </Button>
        <Button
          flex={1}
          w={{ base: 'full', lg: 'container' }}
          variant="primary"
          isDisabled={!isFormValid}
          onPress={handleConfirm}
          _text={{ fontFamily: Typography['fontfamily.base.semibold'] }}>
          CONFIRMAR
        </Button>
      </VStack>
    </VStack>
  );
};
